/**
 * ServiceLegLine — Ted #29 (#1108): ONE DATE of an added service, on the
 * visit's legs surface (the Bookings list), editable on its own.
 *
 * Ted: *"Those charges should then appear automatically at checkout, with an
 * easy way to adjust individual dates or record who performed the service."*
 *
 * A leg row already renders on the Bookings list (it is a booking). This line
 * is what makes it read as a per-date line rather than a stray "Grooming"
 * row: the add-on's own name, THE DATE, who performed it, and the price —
 * and two things the desk can change on this date without touching the
 * others:
 *   · performed by — any staffer, or nobody ("needs a specialist");
 *   · the price, WITH a reason, through the same #906 band Adjust price and
 *     the transport override use (inside 20% for the desk; an owner or
 *     manager past it). Once the leg has an invoice line the server locks it
 *     to the cart (409) and this line shows that sentence — the cart is the
 *     one place that number is changed from then on.
 * Taking the date off is the row's own Cancel (it runs the leg removal path:
 * an open invoice is voided, a settled one refuses) — one control, not two.
 *
 * Design-Philosophy principles served: #1 one glance (what · when · who ·
 * how much on one line), #2 next action obvious (the two edits are named
 * for what they change), #5 never lie (the price shown is the row's own
 * `total_cents`, which IS the charge), #7 built for hands (44px controls,
 * single column at 375).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  adminStaffApi,
  updateServiceLeg,
  type AdminBooking,
  type AdminStaffUser,
} from '../../lib/api.js';
import { pickableStaff, staffOptionLabel } from '../../lib/staff-picker.js';
import { formatServiceDay } from '../../lib/outstanding.js';
import { dollarsToCents } from './AddServiceDialog.js';
import './ServiceLegLine.css';

export interface ServiceLegLineProps {
  readonly apiUrl: string;
  readonly token: string;
  /** The leg row as the schedule endpoint returns it (`isLeg`, `parent_booking_id`). */
  readonly leg: AdminBooking;
  /** Called after a successful change so the list can reload. */
  readonly onChanged: () => void;
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Statuses on which the server accepts an edit; anything else is read-only here. */
const EDITABLE = new Set(['pending', 'confirmed', 'checked_in']);

/**
 * One staff read per page, not one per leg: a stay with 15 walks must not
 * fire 15 identical requests when the desk opens one picker. Cached by API
 * url; a failed read stays null so the picker simply does not open.
 */
const staffCache = new Map<string, Promise<AdminStaffUser[] | null>>();
function loadStaff(apiUrl: string): Promise<AdminStaffUser[] | null> {
  let p = staffCache.get(apiUrl);
  if (!p) {
    p = adminStaffApi
      .getStaff(apiUrl)
      .then((res) => res?.data?.staff ?? null)
      .catch(() => null);
    staffCache.set(apiUrl, p);
  }
  return p;
}
/** Test seam: forget the cached staff read. */
export function resetServiceLegStaffCache(): void {
  staffCache.clear();
}

type Mode = 'idle' | 'who' | 'price';

export function ServiceLegLine({ apiUrl, token, leg, onChanged }: ServiceLegLineProps): JSX.Element {
  const [mode, setMode] = useState<Mode>('idle');
  const [staff, setStaff] = useState<AdminStaffUser[] | null>(null);
  const [who, setWho] = useState<string>(leg.assigned_staff_id ?? '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editable = EDITABLE.has(leg.status);
  const dateIso = leg.date.slice(0, 10);
  const label = leg.add_on_name ?? 'Added service';
  const performedBy = leg.assigned_staff_name ?? null;

  useEffect(() => {
    if (mode !== 'who' || staff !== null) return;
    let live = true;
    void loadStaff(apiUrl).then((list) => {
      if (live) setStaff(list ?? []);
    });
    return () => {
      live = false;
    };
  }, [mode, staff, apiUrl]);

  const saveWho = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await updateServiceLeg(apiUrl, token, leg.parent_booking_id ?? '', leg.id, {
        assignedStaffId: who === '' ? null : who,
      });
      setMode('idle');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save who performed it.');
    } finally {
      setBusy(false);
    }
  }, [apiUrl, token, leg.parent_booking_id, leg.id, who, onChanged]);

  const cents = dollarsToCents(amount);
  const reasonOk = reason.trim().length >= 2;
  const priceReady = cents !== null && reasonOk && cents !== leg.total_cents;

  const savePrice = useCallback(async () => {
    if (cents === null || !reasonOk) return;
    setBusy(true);
    setError(null);
    try {
      await updateServiceLeg(apiUrl, token, leg.parent_booking_id ?? '', leg.id, {
        priceOverride: { totalCents: cents, reason: reason.trim() },
      });
      setMode('idle');
      setAmount('');
      setReason('');
      onChanged();
    } catch (e) {
      // The server's sentence: past the band, locked to the cart, no change.
      setError(e instanceof Error ? e.message : 'Could not change the price.');
    } finally {
      setBusy(false);
    }
  }, [apiUrl, token, leg.parent_booking_id, leg.id, cents, reasonOk, reason, onChanged]);

  return (
    <div className="ferro-leg-line" data-testid={`service-leg-line-${leg.id}`}>
      <div className="ferro-leg-line__facts">
        <span className="ferro-leg-line__label" data-testid={`service-leg-label-${leg.id}`}>
          {label}
        </span>
        <span className="ferro-leg-line__sep" aria-hidden="true">
          ·
        </span>
        <span className="ferro-leg-line__date" data-testid={`service-leg-date-${leg.id}`}>
          {formatServiceDay(dateIso)}
        </span>
        <span className="ferro-leg-line__sep" aria-hidden="true">
          ·
        </span>
        <span className="ferro-leg-line__who" data-testid={`service-leg-performed-by-${leg.id}`}>
          {performedBy ? `Performed by ${performedBy}` : 'Nobody assigned yet'}
        </span>
        <span className="ferro-leg-line__sep" aria-hidden="true">
          ·
        </span>
        <span className="ferro-leg-line__price" data-testid={`service-leg-price-${leg.id}`}>
          {money(leg.total_cents)}
        </span>
      </div>

      {editable && mode === 'idle' ? (
        <div className="ferro-leg-line__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-btn--sm"
            onClick={() => setMode('who')}
            data-testid={`service-leg-edit-who-${leg.id}`}
          >
            {performedBy ? 'Change who did it' : 'Record who did it'}
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-btn--sm"
            onClick={() => setMode('price')}
            data-testid={`service-leg-edit-price-${leg.id}`}
          >
            Adjust this date&rsquo;s price
          </button>
          <span className="ferro-leg-line__hint">
            Cancel takes this date off; the other dates stay.
          </span>
        </div>
      ) : null}

      {editable && mode === 'who' ? (
        <div className="ferro-leg-line__edit" data-testid={`service-leg-who-form-${leg.id}`}>
          {staff === null ? (
            <span className="ferro-leg-line__hint">Loading staff&hellip;</span>
          ) : (
            <label className="ferro-field">
              <span className="ferro-field__label">Performed by</span>
              <select
                id={`service-leg-who-${leg.id}`}
                name="assignedStaffId"
                className="ferro-input"
                value={who}
                disabled={busy}
                onChange={(e) => setWho(e.target.value)}
                data-testid={`service-leg-who-${leg.id}`}
              >
                <option value="">Nobody yet — needs a specialist</option>
                {/* Ted Item 32.1 — current staff only, but the person already
                    recorded on THIS date stays listed (marked as departed) so
                    the history is not blanked or erased by a save. */}
                {pickableStaff(staff, leg.assigned_staff_id).map((s) => (
                  <option key={s.id} value={s.id}>
                    {staffOptionLabel(s)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="ferro-leg-line__buttons">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              disabled={busy}
              onClick={() => {
                setMode('idle');
                setError(null);
                setWho(leg.assigned_staff_id ?? '');
              }}
              data-testid={`service-leg-who-cancel-${leg.id}`}
            >
              Never mind
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={busy || staff === null}
              onClick={() => void saveWho()}
              data-testid={`service-leg-who-save-${leg.id}`}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : null}

      {editable && mode === 'price' ? (
        <div className="ferro-leg-line__edit" data-testid={`service-leg-price-form-${leg.id}`}>
          <label className="ferro-field">
            <span className="ferro-field__label">New price for {formatServiceDay(dateIso)}</span>
            <input
              id={`service-leg-amount-${leg.id}`}
              name="priceAmount"
              className="ferro-input"
              type="text"
              inputMode="decimal"
              placeholder={money(leg.total_cents).slice(1)}
              value={amount}
              disabled={busy}
              onChange={(e) => setAmount(e.target.value)}
              aria-invalid={amount !== '' && cents === null}
              data-testid={`service-leg-amount-${leg.id}`}
            />
            <span className="ferro-field__hint">
              Now {money(leg.total_cents)}. A change bigger than 20% needs an owner or manager.
            </span>
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Why</span>
            <input
              id={`service-leg-reason-${leg.id}`}
              name="priceReason"
              className="ferro-input"
              type="text"
              maxLength={200}
              placeholder="e.g. long walk, 30 min"
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`service-leg-reason-${leg.id}`}
            />
            <span className="ferro-field__hint">Recorded on this date&rsquo;s line, in the visit&rsquo;s history.</span>
          </label>
          <div className="ferro-leg-line__buttons">
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              disabled={busy}
              onClick={() => {
                setMode('idle');
                setError(null);
              }}
              data-testid={`service-leg-price-cancel-${leg.id}`}
            >
              Never mind
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={busy || !priceReady}
              onClick={() => void savePrice()}
              data-testid={`service-leg-price-save-${leg.id}`}
            >
              {busy ? 'Saving…' : cents !== null && priceReady ? `Set ${money(cents)}` : 'Set price'}
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="ferro-leg-line__error" role="alert" data-testid={`service-leg-error-${leg.id}`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
